/**
 * Types for the `/internal/invite-codes/*` endpoints (epic #1475).
 *
 * Invite codes are static — a salesperson may hold several ACTIVE codes at
 * once, each optionally bound to its own model plan (since migration 0102)
 * — and volume-controlled by a per-code daily activation quota measured on
 * the UTC+8 calendar day (no rollover). Code text is
 * server-generated (`<brand prefix>-XXXX-XXXX`, confusable-free charset)
 * unless the caller supplies `code`. A supplied value is trimmed,
 * upper-cased, and must be `<PREFIX>-<SUFFIX>` for the brand
 * (`normalizeCustomInviteCode`); anything else is
 * `INVITE_CODE_FORMAT_INVALID`, and a hit in `invite_codes`,
 * `promoter_codes`, or `gift_cards` is `INVITE_CODE_TAKEN`. `brand`
 * snapshots the owner's `sales_profiles.brand` at creation, and creation
 * fails closed when the owner has no sales profile.
 *
 * Runtime config is the `invite_activation_settings` singleton (eight knobs).
 * `grant_amount_cny_cents` / `grant_expiry_days` / `default_daily_quota` are
 * mint-dialog recommended values — they prefill a new code and are snapshotted
 * onto it as explicit overrides. `max_grant_amount_cny_cents` and the four
 * `login_code_*` knobs are live global config. Per-code `*_override` NULL is
 * only the pre-#2022 legacy fallback (`COALESCE(override, recommended)`);
 * issued grants are never restated.
 *
 * Endpoints:
 *   GET  /internal/invite-codes
 *   POST /internal/invite-codes
 *   GET  /internal/invite-codes/:id
 *   GET  /internal/invite-codes/:id/activations
 *   POST /internal/invite-codes/:id/disable
 *   POST /internal/invite-codes/:id/enable
 *   PUT  /internal/invite-codes/:id/quota
 *   PUT  /internal/invite-codes/:id/code
 *   PUT  /internal/invite-codes/:id/plan
 *   GET  /internal/invite-codes/:id/plan/rebind-preview
 *   POST /internal/invite-codes/:id/plan-from-discounts
 *   GET  /internal/invite-codes/settings
 *   PUT  /internal/invite-codes/settings
 *   GET  /internal/invite-codes/defaults
 *   GET  /internal/invite-codes/salespeople
 *   GET  /internal/invite-codes/rate-discount-targets
 */

import type {
  ModelPlanDetail,
  ModelPlanPropagationRunSummary,
  ModelPlanSkuDeltas,
} from './internal-model-plans.ts';

export type InviteCodeStatus = 'active' | 'disabled';

/**
 * Brand → code-text prefix. Unknown brands fall back to
 * `INVITE_CODE_FALLBACK_PREFIX`; the row's `brand` stays the attribution.
 */
export const INVITE_CODE_BRAND_PREFIXES: Readonly<Record<string, string>> = {
  nadoutong: 'NDT',
  nxtpath: 'NXT',
};

export const INVITE_CODE_FALLBACK_PREFIX = 'INV';

export function inviteCodePrefixForBrand(brand: string): string {
  return INVITE_CODE_BRAND_PREFIXES[brand] ?? INVITE_CODE_FALLBACK_PREFIX;
}

export const CUSTOM_INVITE_CODE_SUFFIX_MIN_LENGTH = 2;
export const CUSTOM_INVITE_CODE_SUFFIX_MAX_LENGTH = 16;

/**
 * Normalize caller-supplied invite-code text for `brand`.
 *
 * Trim, upper-case, then require `<prefix>-<suffix>` where `prefix` is
 * `inviteCodePrefixForBrand(brand)` and `suffix` is 2–16 chars of
 * `[A-Z0-9-]`, alphanumeric at both ends, with no `--`. Returns the
 * normalized full code, or null when the input is not that shape.
 */
export function normalizeCustomInviteCode(raw: unknown, brand: string): string | null {
  if (typeof raw !== 'string') return null;
  const normalized = raw.trim().toUpperCase();
  const prefix = inviteCodePrefixForBrand(brand);
  const head = `${prefix}-`;
  if (!normalized.startsWith(head)) return null;
  const suffix = normalized.slice(head.length);
  if (
    suffix.length < CUSTOM_INVITE_CODE_SUFFIX_MIN_LENGTH ||
    suffix.length > CUSTOM_INVITE_CODE_SUFFIX_MAX_LENGTH
  ) {
    return null;
  }
  if (!/^[A-Z0-9-]+$/.test(suffix)) return null;
  const first = suffix[0];
  const last = suffix[suffix.length - 1];
  if (first === undefined || last === undefined) return null;
  if (!/^[A-Z0-9]$/.test(first) || !/^[A-Z0-9]$/.test(last)) return null;
  if (suffix.includes('--')) return null;
  return normalized;
}

export interface InviteCode {
  id: string;
  code: string;
  owner_ops_user_id: string;
  /**
   * Owner's readable name, resolved live from `ops_users` through the same
   * fallback chain the mint dialog's `InviteCodeSalesperson.display_name`
   * uses, so one person reads identically in both surfaces (#1948). Null
   * only when the owner has no `ops_users` row at all; a *disabled* owner
   * still resolves, because their historical codes must stay readable.
   * `owner_ops_user_id` is never replaced by it — it stays on the wire as
   * the stable troubleshooting handle.
   */
  owner_display_name: string | null;
  /** Owner's `ops_users.email`; null under the same missing-row condition. */
  owner_email: string | null;
  /** Snapshot of the owner's `sales_profiles.brand` at creation. */
  brand: string;
  /** Per-code daily quota; null (legacy rows only) falls back to `default_daily_quota`. */
  daily_quota_override: number | null;
  /** Resolved quota actually enforced today: `daily_quota_override ?? default_daily_quota`. */
  effective_daily_quota: number;
  /** Per-code trial grant face value in CNY cents; null is the legacy inherit fallback. */
  grant_amount_cny_cents_override: number | null;
  /** Resolved grant face value: `grant_amount_cny_cents_override ?? grant_amount_cny_cents`. */
  effective_grant_amount_cny_cents: number;
  /** Per-code trial grant lifetime in days; null is the legacy inherit fallback. */
  grant_expiry_days_override: number | null;
  /** Resolved grant lifetime: `grant_expiry_days_override ?? grant_expiry_days`. */
  effective_grant_expiry_days: number;
  status: InviteCodeStatus;
  created_by_ops_user_id: string;
  created_at: string;
  disabled_at: string | null;
  disabled_by_ops_user_id: string | null;
  updated_at: string;
  /** Read model: activations already consumed today (UTC+8 calendar day). */
  today_activation_count: number;
  /**
   * One-shot rate discounts snapshotted onto this code. Empty means the
   * customer keeps platform list prices. Edits never restate already-
   * activated orgs. Mutually exclusive with a bound `model_plan`.
   */
  rate_discounts: InviteCodeRateDiscount[];
  /**
   * Live model-plan binding. Null on legacy (0064 snapshot) codes.
   * Joined from `model_plans`; the id is the follow handle, the name is
   * display-only and refreshes if the plan is renamed.
   */
  model_plan: InviteCodeModelPlanRef | null;
}

/** Display join for a code's bound plan. */
export interface InviteCodeModelPlanRef {
  id: string;
  name: string;
}

/** Query params for GET /internal/invite-codes (allowlisted by ops-api). */
export interface ListInviteCodesQuery {
  owner_ops_user_id?: string;
  status?: InviteCodeStatus;
  brand?: string;
}

export interface ListInviteCodesResponse {
  invite_codes: InviteCode[];
}

/**
 * Code text is server-generated (`<brand prefix>-XXXX-XXXX`) when `code`
 * is omitted or null; collisions retry server-side. A supplied `code` is
 * normalized with `normalizeCustomInviteCode` and minted exactly once —
 * a uniqueness hit is `INVITE_CODE_TAKEN`, never a retry with other text.
 */
export interface CreateInviteCodeRequest {
  owner_ops_user_id: string;
  /**
   * Optional caller-chosen code text. Omitted or null keeps server
   * generation. A string is trimmed and upper-cased, then must match
   * `<PREFIX>-<SUFFIX>` for the owner's brand; anything else is
   * `INVITE_CODE_FORMAT_INVALID`.
   */
  code?: string | null;
  /**
   * Positive integer. The mint dialog always sends one. Omitted is the
   * legacy API path (stores NULL; activation coalesces the recommended
   * value). `null` is not inherit at create time and is rejected.
   */
  daily_quota_override?: number;
  grant_amount_cny_cents_override?: number;
  grant_expiry_days_override?: number;
  /**
   * Optional. Omitted or [] stores no discounts. Rejected if any row fails the sales-tier floor.
   *
   * @deprecated Bind a model plan (`model_plan_id`) instead. Per-code
   * one-shot discounts remain readable for codes minted before plans;
   * new writes should go through model plans. A leftover list blocks
   * plan bind (`INVITE_CODE_HAS_LEGACY_DISCOUNTS`) — migrate with
   * `POST /internal/invite-codes/:id/plan-from-discounts`.
   */
  rate_discounts?: InviteCodeRateDiscountInput[];
  /**
   * Optional live plan binding. Mutually exclusive with a non-empty
   * `rate_discounts` (400). The plan must exist, be `active`, and match
   * the brand snapshotted from the owner's sales profile.
   */
  model_plan_id?: string;
}

export interface CreateInviteCodeResponse {
  invite_code: InviteCode;
}

/**
 * PUT /internal/invite-codes/:id/code — replace a code's text.
 *
 * The previous text is released immediately (no reservation). Disabled
 * codes may be renamed. A value equal to the current text is a no-op
 * 200 with no audit row.
 */
export interface UpdateInviteCodeTextRequest {
  code: string;
}

export interface UpdateInviteCodeTextResponse {
  invite_code: InviteCode;
}

/**
 * GET /internal/invite-codes/:id — single-code read.
 *
 * ops-api resolves a scoped session's authority over an `:id`-addressed
 * mutation through this endpoint before proxying it, so the brand check has
 * one cheap lookup instead of scanning a filtered list.
 */
export interface GetInviteCodeResponse {
  invite_code: InviteCode;
}

/**
 * One customer this code brought in (#1985).
 *
 * A DERIVED read model, not an activity table: every activation issues the
 * trial grant through `issueGiftDirect` with `reason = 'invite_activation'`
 * and `invite_code_id` in the grant's metadata, so the grant rows ARE the
 * activation record. Nothing writes an activation any other way, and there
 * is no separate table to backfill.
 */
export interface InviteCodeActivation {
  account_id: string;
  org_id: string;
  /** The activated account's login email — the code's actual referral. */
  email: string;
  /** Name of the org the activation created. */
  org_name: string;
  /** When the trial grant was issued, i.e. the activation instant. */
  activated_at: string;
  /** Trial grant face value, snapshot at activation and never restated. */
  grant_amount_cny_cents: number;
  grant_expires_at: string;
  /**
   * Snapshot of what this activation did with the code's rate discounts.
   * Null on pre-#2022-B grants that never carried the key. Never includes
   * procurement-derived numbers — this list is CM-reachable.
   */
  rate_discounts: InviteCodeActivationRateDiscounts | null;
}

/**
 * GET /internal/invite-codes/:id/activations.
 *
 * `activations` is newest-first and capped at 200 rows. `total` counts every
 * activation the code ever produced and is deliberately NOT affected by that
 * cap — the "N customers invited" headline must stay right once a code
 * outgrows one page.
 */
export interface ListInviteCodeActivationsResponse {
  activations: InviteCodeActivation[];
  total: number;
}

export interface DisableInviteCodeResponse {
  invite_code: InviteCode;
}

export interface EnableInviteCodeResponse {
  invite_code: InviteCode;
}

/**
 * PUT /internal/invite-codes/:id/quota — per-code activation config.
 *
 * Each field is independently optional. `null` clears that override so the
 * code re-inherits the singleton; omitting the field leaves the stored
 * override untouched. Those two are distinct: an empty body is invalid,
 * not a clear.
 */
export interface UpdateInviteCodeQuotaRequest {
  daily_quota_override?: number | null;
  grant_amount_cny_cents_override?: number | null;
  grant_expiry_days_override?: number | null;
  /**
   * Replace-all when present. `[]` clears every discount. Omitting the
   * field leaves stored discounts untouched. Already-activated orgs are
   * never restated.
   *
   * @deprecated Bind a model plan (`PUT /internal/invite-codes/:id/plan`)
   * instead. Per-code one-shot discounts remain readable for codes minted
   * before plans; new writes should go through model plans. A leftover
   * list blocks plan bind (`INVITE_CODE_HAS_LEGACY_DISCOUNTS`) — migrate
   * with `POST /internal/invite-codes/:id/plan-from-discounts`.
   */
  rate_discounts?: InviteCodeRateDiscountInput[];
}

export interface UpdateInviteCodeQuotaResponse {
  invite_code: InviteCode;
}

/**
 * PUT /internal/invite-codes/:id/plan — bind, rebind, or unbind a model
 * plan. The default write touches only `invite_codes.model_plan_id`
 * (future activations). `restate_activated: true` (admin) also rebinds
 * every org still attributed to this code
 * (`org_model_plan_assignments.invite_code_id`).
 *
 * Bind/rebind require the plan to be active, same-brand as the code, and
 * the code to carry zero `invite_code_rate_discounts` rows — otherwise
 * `INVITE_CODE_HAS_LEGACY_DISCOUNTS`. Unbind (`null`) is always allowed
 * for the future-only write; restating an unbind is refused.
 */
export interface UpdateInviteCodePlanRequest {
  model_plan_id: string | null;
  /** Admin-only. After the future-only rebind, restate historically-activated orgs. */
  restate_activated?: boolean;
}

export interface UpdateInviteCodePlanResponse {
  invite_code: InviteCode;
  propagation?: ModelPlanPropagationRunSummary;
}

/** GET /internal/invite-codes/:id/plan/rebind-preview */
export interface InviteCodePlanRebindPreviewOrg {
  org_id: string;
  current_model_plan_id: string;
  current_model_plan_name: string;
  sku_deltas: ModelPlanSkuDeltas;
}

export interface InviteCodePlanRebindPreviewResponse {
  invite_code_id: string;
  target_model_plan_id: string | null;
  target_model_plan_name: string | null;
  orgs: InviteCodePlanRebindPreviewOrg[];
}

/**
 * POST /internal/invite-codes/:id/plan-from-discounts — create a plan
 * from this code's leftover `invite_code_rate_discounts` rows, clear
 * those rows, and bind the new plan, in one transaction (#2298).
 *
 * Brand is the code's snapshotted brand. Floor validation is the shared
 * `preparePlanContents` path (sales tier, no approval hatch).
 */
export interface CreatePlanFromInviteCodeDiscountsRequest {
  name: string;
  description?: string;
}

export interface CreatePlanFromInviteCodeDiscountsResponse {
  model_plan: ModelPlanDetail;
  invite_code: InviteCode;
}

// ─── Per-code one-shot rate discounts (#2022 Part B) ─────────────────────────

export type InviteCodeRateDiscountGrain = 'sku' | 'namespace';

/**
 * One discount row stored on a code. `target_label_snapshot` is taken at
 * write time (`<namespace_slug>/<model>` or the namespace slug) and compared
 * at activation; a rename that keeps the id is treated as a different
 * upstream and skipped.
 */
export interface InviteCodeRateDiscount {
  grain: InviteCodeRateDiscountGrain;
  target_id: string;
  target_label_snapshot: string;
  rate_multiplier: number;
}

/** Write payload: the server fills `target_label_snapshot` from live catalog. */
export interface InviteCodeRateDiscountInput {
  grain: InviteCodeRateDiscountGrain;
  target_id: string;
  rate_multiplier: number;
}

export type InviteCodeRateDiscountSkipReason =
  | 'target_missing'
  | 'label_mismatch'
  | 'not_listed'
  | 'below_floor'
  | 'unevaluable';

export interface InviteCodeActivationRateDiscountApplied {
  grain: InviteCodeRateDiscountGrain;
  target_id: string;
  target_label: string;
  rate_multiplier: number;
}

export interface InviteCodeActivationRateDiscountSkipped {
  grain: InviteCodeRateDiscountGrain;
  target_id: string;
  target_label: string;
  rate_multiplier: number;
  reason: InviteCodeRateDiscountSkipReason;
}

export interface InviteCodeActivationRateDiscounts {
  applied: InviteCodeActivationRateDiscountApplied[];
  skipped: InviteCodeActivationRateDiscountSkipped[];
}

// ─── Plan-bound activation materialization (#2295) ──────────────────────────

/**
 * One write the activation materializer attempted. `kind` is the row
 * family, parallel to rate-discount `grain`. `rate_multiplier` is set
 * only on `sku_rate` rows.
 */
export type ModelPlanMaterializationKind =
  | 'assignment'
  | 'namespace_backstop'
  | 'sku_rate'
  | 'sku_visibility'
  | 'plan';

export type ModelPlanMaterializationSkipReason =
  | 'target_missing'
  | 'below_floor'
  | 'conflict'
  | 'unevaluable'
  | 'plan_unavailable';

export interface ModelPlanMaterializationApplied {
  kind: Exclude<ModelPlanMaterializationKind, 'plan'>;
  target_id: string;
  target_label: string;
  rate_multiplier: number | null;
}

export interface ModelPlanMaterializationSkipped {
  kind: ModelPlanMaterializationKind;
  target_id: string;
  target_label: string;
  rate_multiplier: number | null;
  reason: ModelPlanMaterializationSkipReason;
}

/**
 * Snapshot of what a plan-bound activation wrote (or skipped). Rides
 * gift metadata under `model_plan_materialization`, the plan-bound
 * twin of `rate_discounts`.
 */
export interface ModelPlanMaterializationSummary {
  plan_id: string | null;
  plan_name: string | null;
  applied: ModelPlanMaterializationApplied[];
  skipped: ModelPlanMaterializationSkipped[];
}

/**
 * GET /internal/invite-codes/rate-discount-targets
 *
 * Listed SKUs grouped by namespace, with the *sales* floor only. `null`
 * means no sales cost is configured for that SKU × brand — never a
 * procurement-derived number. Lead/admin only.
 */
export interface InviteCodeRateDiscountTargetSku {
  id: string;
  model: string;
  label: string;
  sales_floor_multiplier: number | null;
}

export interface InviteCodeRateDiscountTargetNamespace {
  id: string;
  slug: string;
  sales_floor_multiplier: number | null;
  /**
   * True when at least one listed SKU on this line has no sales cost for
   * the brand. The sales-floor hint is then only the max of configured
   * SKUs; write-time still applies the procurement hard floor to the
   * unconfigured ones. Never a procurement number.
   */
  has_unconfigured_sales_cost: boolean;
  skus: InviteCodeRateDiscountTargetSku[];
}

/** Cap on `rate_discounts` entries per code (mint and replace-all). */
export const INVITE_CODE_RATE_DISCOUNT_MAX_ITEMS = 20;

export interface GetInviteCodeRateDiscountTargetsResponse {
  namespaces: InviteCodeRateDiscountTargetNamespace[];
}

// ─── Runtime settings (singleton id = 1) ─────────────────────────────────────

export interface InviteActivationSettings {
  /** Mint-dialog recommended trial grant, CNY cents. Not live-inherited by pinned codes. */
  grant_amount_cny_cents: number;
  /** Mint-dialog recommended trial grant lifetime in days. */
  grant_expiry_days: number;
  /** Mint-dialog recommended per-code daily activation quota. */
  default_daily_quota: number;
  /**
   * Live cap on setting a per-code grant override (mint, or an edit that
   * actually changes the stored grant). Seeded at 50000 (¥500). The
   * recommended grant amount must not exceed this.
   */
  max_grant_amount_cny_cents: number;
  /** Login-code lifetime in seconds (also echoed as `expiresInSeconds`). */
  login_code_ttl_seconds: number;
  /** Minimum interval between sends to the same email, in seconds. */
  login_code_resend_interval_seconds: number;
  /** Max login-code sends per email per UTC+8 calendar day. */
  login_code_daily_send_limit: number;
  /** Failed verify attempts before the open code is invalidated. */
  login_code_max_attempts: number;
  updated_at: string | null;
  updated_by_ops_user_id: string | null;
}

export interface GetInviteActivationSettingsResponse {
  settings: InviteActivationSettings;
}

/** Partial update: at least one field must be present. */
export interface UpdateInviteActivationSettingsRequest {
  grant_amount_cny_cents?: number;
  grant_expiry_days?: number;
  default_daily_quota?: number;
  max_grant_amount_cny_cents?: number;
  login_code_ttl_seconds?: number;
  login_code_resend_interval_seconds?: number;
  login_code_daily_send_limit?: number;
  login_code_max_attempts?: number;
}

/**
 * GET /internal/invite-codes/defaults — the three mint-dialog prefills plus
 * the per-code grant cap. Deliberately omits `login_code_*`: those knobs
 * govern the email login-code path, which has no invite code, and must not
 * leak to a sales-lead session that can mint but cannot read /settings.
 */
export interface InviteActivationMintDefaults {
  grant_amount_cny_cents: number;
  grant_expiry_days: number;
  default_daily_quota: number;
  max_grant_amount_cny_cents: number;
}

export interface GetInviteActivationMintDefaultsResponse {
  defaults: InviteActivationMintDefaults;
}

export interface UpdateInviteActivationSettingsResponse {
  settings: InviteActivationSettings;
}

// ─── Salesperson candidates (for the mint-dialog owner picker) ──────────────

/**
 * A same-brand candidate owner for a new invite code. Eligibility is "has a
 * `sales_profiles` row in this brand and does not already hold an ACTIVE
 * invite code" (see `listSalespeopleInBrand`) — no customer-manager role
 * required, unlike the similarly-shaped `customer_transfer` candidate list.
 */
export interface InviteCodeSalesperson {
  ops_user_id: string;
  email: string;
  display_name: string;
  brand: string;
}

export interface ListInviteCodeSalespeopleResponse {
  salespeople: InviteCodeSalesperson[];
}
